import { useState, useCallback, useMemo } from 'react';
import { Plus, BarChart3, ClipboardList, PlusCircle, Search, Package, AlertTriangle } from 'lucide-react';
import {
  type StackCompound,
  type TierKey,
  type FrequencyKey,
  PRESETS,
  TIER_NAMES,
  TIER_ICONS,
  TIER_COLORS,
  FREQ_LABELS,
  COMPOUND_COLORS,
} from '../data/peptidePresets';
import { getPatientMetrics, calcCompound, fmtMg } from '../utils/peptideCalculations';
import { useFormularyCompounds, type FormularyFamily, type FormularySku } from '../hooks/useFormularyCompounds';
import { PatientProfileSection } from '../components/peptides/PatientProfileSection';
import { CompoundCard } from '../components/peptides/CompoundCard';
import { StackAssessment } from '../components/peptides/StackAssessment';
import { OrderPreview } from '../components/peptides/OrderPreview';

const CONC_UNIT_FROM_PARSE: Record<'mg' | 'mcg' | 'units', 'mg/mL' | 'mcg/mL' | 'units/mL'> = {
  mg: 'mg/mL', mcg: 'mcg/mL', units: 'units/mL',
};

const REGULATORY_BADGES: Record<string, { label: string; className: string }> = {
  fda: { label: 'FDA Approved', className: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20' },
  investigational: { label: 'Investigational', className: 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20' },
  research: { label: 'Research / Off-label', className: 'bg-violet-500/10 text-violet-600 dark:text-violet-400 border-violet-500/20' },
  cat2: { label: 'FDA Cat. 2 — Prohibited', className: 'bg-rose-500/10 text-rose-600 dark:text-rose-400 border-rose-500/20' },
};

export default function PeptideCalculator() {
  // Patient state
  const [sex, setSex] = useState<'male' | 'female'>('male');
  const [age, setAge] = useState(38);
  const [weight, setWeight] = useState(210);
  const [weightUnit, setWeightUnit] = useState<'lbs' | 'kg'>('lbs');
  const [height, setHeight] = useState(73);
  const [heightUnit, setHeightUnit] = useState<'in' | 'cm'>('in');

  // Formulary browser state
  const { families, loading: formularyLoading } = useFormularyCompounds();
  const [familySearch, setFamilySearch] = useState('');
  const [selectedFamilySlug, setSelectedFamilySlug] = useState<string | null>(null);
  const [selectedProductId, setSelectedProductId] = useState<string | null>(null);

  // Compound form state
  const [selectedPreset, setSelectedPreset] = useState<string | null>(null);
  const [selectedTier, setSelectedTier] = useState<TierKey | null>(null);
  const [formName, setFormName] = useState('');
  const [formConc, setFormConc] = useState<number | ''>('');
  const [formConcUnit, setFormConcUnit] = useState<'mg/mL' | 'mcg/mL' | 'units/mL'>('mg/mL');
  const [formVial, setFormVial] = useState(5);
  const [formDose, setFormDose] = useState<number | ''>('');
  const [formDoseUnit, setFormDoseUnit] = useState<'mcg' | 'mg' | 'units'>('mcg');
  const [formFreq, setFormFreq] = useState<FrequencyKey>('daily');
  const [formRoute, setFormRoute] = useState<'SubQ' | 'IM' | 'IV'>('SubQ');

  // Stack state
  const [compounds, setCompounds] = useState<StackCompound[]>([]);
  const [nextId, setNextId] = useState(0);
  const [expandedCards, setExpandedCards] = useState<Set<number>>(new Set());

  // Patient metrics
  const metrics = useMemo(
    () => getPatientMetrics(sex, age, weight, weightUnit, height, heightUnit),
    [sex, age, weight, weightUnit, height, heightUnit]
  );

  // Selected family / SKU (declared early so addCompound can reference selectedSku)
  const selectedFamily = useMemo(
    () => families.find(f => f.slug === selectedFamilySlug) || null,
    [families, selectedFamilySlug]
  );
  const selectedSku = useMemo(
    () => selectedFamily?.skus.find(s => s.productId === selectedProductId) || null,
    [selectedFamily, selectedProductId]
  );

  // Handle patient field changes
  const handlePatientChange = useCallback((field: string, value: string | number) => {
    switch (field) {
      case 'sex': setSex(value as 'male' | 'female'); break;
      case 'age': setAge(value as number); break;
      case 'weight': setWeight(value as number); break;
      case 'weightUnit': setWeightUnit(value as 'lbs' | 'kg'); break;
      case 'height': setHeight(value as number); break;
      case 'heightUnit': setHeightUnit(value as 'in' | 'cm'); break;
    }
  }, []);

  // Selecting a family loads its clinical preset (if any) as the dosing brain
  // and defaults the form from the preset until a real SKU is chosen.
  const selectFamily = useCallback((fam: FormularyFamily) => {
    setSelectedFamilySlug(fam.slug);
    setSelectedProductId(null);
    if (fam.preset && fam.presetKey) {
      const p = fam.preset;
      setSelectedPreset(fam.presetKey);
      setFormName(p.name);
      setFormConc(p.conc);
      setFormConcUnit(p.concUnit);
      setFormVial(p.vial);
      setFormRoute(p.route);
      setFormDoseUnit(p.tiers.moderate.doseUnit);
      selectTier(fam.presetKey, 'moderate');
    } else {
      setSelectedPreset(null);
      setSelectedTier(null);
      setFormName(fam.label);
    }
  }, []);

  // Selecting a SKU overrides concentration/vial with the REAL catalog values.
  const selectSku = useCallback((sku: FormularySku) => {
    setSelectedProductId(sku.productId);
    setFormName(sku.drugName);
    if (sku.conc.calcReady && sku.conc.value != null && sku.conc.unit) {
      setFormConc(sku.conc.value);
      setFormConcUnit(CONC_UNIT_FROM_PARSE[sku.conc.unit]);
    }
    if (sku.vialMl != null) setFormVial(sku.vialMl);
  }, []);

  const selectTier = useCallback((presetKey: string, tier: TierKey) => {
    const p = PRESETS[presetKey];
    if (!p) return;
    const t = p.tiers[tier];
    if (!t) return;
    setSelectedTier(tier);
    setFormDoseUnit(t.doseUnit);
    setFormDose(t.dose);
    setFormFreq(t.freq);
  }, []);

  // Live preview
  const livePreview = useMemo(() => {
    const conc = typeof formConc === 'number' ? formConc : 0;
    const dose = typeof formDose === 'number' ? formDose : 0;
    if (!conc || !dose) return null;
    return calcCompound({ conc, concUnit: formConcUnit, vial: formVial, dose, doseUnit: formDoseUnit, freq: formFreq });
  }, [formConc, formConcUnit, formVial, formDose, formDoseUnit, formFreq]);

  // Add compound to stack
  const addCompound = useCallback(() => {
    const conc = typeof formConc === 'number' ? formConc : 0;
    const dose = typeof formDose === 'number' ? formDose : 0;
    if (!formName || !conc || !dose) return;

    let tierInfo = '', timelineInfo = '', synergyNote = '', warnInfo = '';
    if (selectedPreset && selectedTier && PRESETS[selectedPreset]) {
      const p = PRESETS[selectedPreset];
      tierInfo = p.tiers[selectedTier]?.info || '';
      timelineInfo = p.timeline?.[selectedTier] || '';
      warnInfo = p.warnings?.[selectedTier] || '';
      if (p.synergy?.length) {
        synergyNote = p.synergy.map(s => `+ ${s.label}: ${s.note}`).join(' · ');
      }
    }

    const id = nextId;
    setNextId(prev => prev + 1);
    setExpandedCards(prev => new Set([...prev, id]));
    setCompounds(prev => [...prev, {
      id, name: formName, conc, concUnit: formConcUnit, vial: formVial,
      dose, doseUnit: formDoseUnit, freq: formFreq, route: formRoute,
      tier: selectedTier || 'custom', tierInfo, timelineInfo, synergyNote, warnInfo,
      productId: selectedSku?.productId,
      productName: selectedSku?.displayName,
      unitPrice: selectedSku?.price ?? undefined,
      outOfStock: selectedSku?.outOfStock,
    }]);
    clearForm();
  }, [formName, formConc, formConcUnit, formVial, formDose, formDoseUnit, formFreq, formRoute, selectedPreset, selectedTier, selectedSku, nextId]);

  const clearForm = useCallback(() => {
    setFormName('');
    setFormConc('');
    setFormVial(5);
    setFormDose('');
    setFormDoseUnit('mcg');
    setFormFreq('daily');
    setFormRoute('SubQ');
    setSelectedPreset(null);
    setSelectedTier(null);
    setSelectedFamilySlug(null);
    setSelectedProductId(null);
  }, []);

  const removeCompound = useCallback((id: number) => {
    setCompounds(prev => prev.filter(c => c.id !== id));
    setExpandedCards(prev => { const n = new Set(prev); n.delete(id); return n; });
  }, []);

  const toggleCard = useCallback((id: number) => {
    setExpandedCards(prev => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  }, []);

  const updateCompoundField = useCallback((id: number, field: string, value: string | number) => {
    setCompounds(prev => prev.map(c => {
      if (c.id !== id) return c;
      const updated = { ...c, [field]: value };
      if (['conc', 'vial', 'dose', 'doseUnit', 'freq'].includes(field)) {
        updated.tier = 'custom';
        updated.tierInfo = '';
        updated.timelineInfo = '';
        updated.warnInfo = '';
      }
      return updated;
    }));
  }, []);

  // Formulary search list (selection memos live near the top so addCompound can use them)
  const filteredFamilies = useMemo(() => {
    const q = familySearch.trim().toLowerCase();
    if (!q) return families;
    return families.filter(f =>
      f.label.toLowerCase().includes(q) ||
      f.slug.toLowerCase().includes(q) ||
      f.skus.some(s => s.drugName.toLowerCase().includes(q))
    );
  }, [families, familySearch]);

  // Current preset data (for info boxes)
  const currentPreset = selectedPreset ? PRESETS[selectedPreset] : null;
  const currentTierData = currentPreset && selectedTier ? currentPreset.tiers[selectedTier] : null;

  // Summary calculations
  const summaryData = useMemo(() => {
    let totalWkMg = 0;
    let totalDailyInj = 0;
    compounds.forEach(c => {
      const r = calcCompound(c);
      totalWkMg += r.wkMg;
      totalDailyInj += r.pw / 7;
    });
    return { totalWkMg, totalDailyInj };
  }, [compounds]);

  return (
    <div className="max-w-4xl mx-auto space-y-4">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-2.5">
          <div className="w-2.5 h-2.5 rounded-full bg-blue-500 shadow-[0_0_12px_rgba(79,143,255,0.5)]" />
          <h1 className="text-xl font-bold tracking-tight">Injectable Peptide Calculator</h1>
        </div>
        <p className="text-xs text-neutral-400 ml-5 mt-0.5">
          Clinical dosing reference · 5-tier protocols · Stack analysis
          <span className="font-mono text-[10px] opacity-50 ml-1">v4.1</span>
        </p>
      </div>

      {/* Patient Profile */}
      <PatientProfileSection
        sex={sex} age={age} weight={weight} weightUnit={weightUnit}
        height={height} heightUnit={heightUnit} metrics={metrics}
        onChange={handlePatientChange}
      />

      {/* Add Compound */}
      <div className="bg-white dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 rounded-xl shadow-sm p-5">
        <h2 className="text-sm font-bold flex items-center gap-2 mb-4">
          <Plus className="w-4 h-4" />
          Add Compound
        </h2>

        {/* Formulary browser — pick a real catalog family, then a SKU */}
        {!selectedFamily ? (
          <div className="mb-3">
            <div className="relative mb-2">
              <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-neutral-400" />
              <input
                type="text"
                value={familySearch}
                onChange={e => setFamilySearch(e.target.value)}
                placeholder="Search formulary (e.g. semaglutide, BPC-157)…"
                className="w-full bg-neutral-50 dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-700 rounded-lg pl-8 pr-2.5 py-2 text-sm outline-none focus:border-blue-500 transition-colors"
              />
            </div>
            {formularyLoading ? (
              <div className="text-[11px] text-neutral-400 py-6 text-center">Loading formulary…</div>
            ) : filteredFamilies.length === 0 ? (
              <div className="text-[11px] text-neutral-400 py-6 text-center">No matching products.</div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5 max-h-72 overflow-y-auto pr-1">
                {filteredFamilies.map(fam => (
                  <button
                    key={fam.slug}
                    onClick={() => selectFamily(fam)}
                    className="text-left rounded-lg border border-neutral-200 dark:border-neutral-700 hover:border-blue-400 dark:hover:border-blue-500 px-2.5 py-2 transition-all"
                  >
                    <div className="flex items-center gap-1.5">
                      <span className="text-[12px] font-semibold text-neutral-700 dark:text-neutral-200 truncate">{fam.label}</span>
                      {fam.preset && (
                        <span className="text-[8px] font-bold uppercase tracking-wider text-blue-500 bg-blue-500/10 rounded px-1 py-px shrink-0">Rx</span>
                      )}
                    </div>
                    <div className="text-[10px] text-neutral-400 mt-0.5">
                      {fam.skus.length} SKU{fam.skus.length !== 1 ? 's' : ''}
                      {fam.calcReadyCount > 0 && <span className="text-emerald-500"> · {fam.calcReadyCount} calc-ready</span>}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="mb-3">
            <button
              onClick={() => { setSelectedFamilySlug(null); setSelectedProductId(null); }}
              className="text-[11px] text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300 mb-2 inline-flex items-center gap-1"
            >
              ← All families
            </button>
            <div className="flex items-center gap-1.5 mb-2">
              <Package className="w-3.5 h-3.5 text-blue-500" />
              <span className="text-[13px] font-semibold text-neutral-700 dark:text-neutral-200">{selectedFamily.label}</span>
              <span className="text-[10px] text-neutral-400">· pick a SKU to load its real concentration</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
              {selectedFamily.skus.map(sku => {
                const isSel = sku.productId === selectedProductId;
                return (
                  <button
                    key={sku.productId}
                    onClick={() => selectSku(sku)}
                    disabled={!sku.calcReady}
                    className={`text-left rounded-lg border px-2.5 py-2 transition-all ${
                      isSel
                        ? 'border-blue-500 bg-blue-500/5'
                        : sku.calcReady
                          ? 'border-neutral-200 dark:border-neutral-700 hover:border-blue-400 dark:hover:border-blue-500'
                          : 'border-neutral-100 dark:border-neutral-800 opacity-50 cursor-not-allowed'
                    }`}
                  >
                    <div className="flex items-center justify-between gap-1.5">
                      <span className="text-[12px] font-medium text-neutral-700 dark:text-neutral-200 truncate">{sku.displayName}</span>
                      {typeof sku.price === 'number' && (
                        <span className="text-[11px] font-mono text-neutral-500 shrink-0">${sku.price.toFixed(2)}</span>
                      )}
                    </div>
                    <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                      {sku.calcReady && sku.conc.value != null ? (
                        <span className="text-[10px] font-mono text-emerald-600 dark:text-emerald-400">
                          {sku.conc.value} {sku.conc.unit}/mL · {sku.vialMl} mL vial
                        </span>
                      ) : (
                        <span className="text-[10px] text-amber-500 inline-flex items-center gap-1">
                          <AlertTriangle className="w-3 h-3" /> {sku.conc.note || 'not calc-ready'}
                        </span>
                      )}
                      {sku.conc.assumedPerMl && (
                        <span className="text-[9px] text-amber-500" title={sku.conc.note}>assumed /mL</span>
                      )}
                      {sku.outOfStock && <span className="text-[9px] text-rose-500 font-semibold">OUT OF STOCK</span>}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Regulatory badge */}
        {currentPreset && (
          <div className="mb-2">
            <span className={`inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded border ${REGULATORY_BADGES[currentPreset.regulatory]?.className}`}>
              {REGULATORY_BADGES[currentPreset.regulatory]?.label}
            </span>
            {currentPreset.regNote && (
              <span className="text-[10px] text-neutral-400 ml-2">{currentPreset.regNote}</span>
            )}
          </div>
        )}

        {/* Synergy notes */}
        {currentPreset?.synergy?.length ? (
          <div className="mb-2 space-y-1">
            {currentPreset.synergy.map((s, i) => (
              <div key={i} className="rounded-md px-3 py-2 text-[11px] leading-relaxed bg-emerald-500/5 border border-emerald-500/10 text-emerald-600 dark:text-emerald-400 flex items-start gap-1.5">
                <span>🔗</span>
                <span><strong>+ {s.label}:</strong> {s.note}</span>
              </div>
            ))}
          </div>
        ) : null}

        {/* Tier selection */}
        {selectedPreset && (
          <div className="mt-3 mb-3">
            <label className="block text-[10px] font-semibold uppercase tracking-wider text-neutral-500 dark:text-neutral-400 mb-1.5">Dosing Tier</label>
            <div className="grid grid-cols-5 gap-1.5">
              {(Object.keys(TIER_NAMES) as TierKey[]).map(tier => {
                const isActive = selectedTier === tier;
                const colors = TIER_COLORS[tier];
                return (
                  <button
                    key={tier}
                    onClick={() => selectTier(selectedPreset, tier)}
                    className={`py-2 px-1 rounded-lg border-[1.5px] text-center transition-all ${
                      isActive
                        ? `${colors.border} ${colors.text} ${colors.bg}`
                        : 'border-neutral-200 dark:border-neutral-700 text-neutral-400 hover:border-neutral-300 dark:hover:border-neutral-600 hover:text-neutral-600 dark:hover:text-neutral-300'
                    }`}
                  >
                    <div className="text-sm">{TIER_ICONS[tier]}</div>
                    <div className="text-[10px] font-semibold">{TIER_NAMES[tier]}</div>
                  </button>
                );
              })}
            </div>

            {/* Tier info */}
            {currentTierData && (
              <div className="mt-2 rounded-md px-3 py-2 text-[11px] leading-relaxed bg-blue-500/5 border border-blue-500/10 text-blue-600 dark:text-blue-400">
                {currentTierData.info}
              </div>
            )}
            {currentPreset?.timeline?.[selectedTier!] && (
              <div className="mt-1.5 rounded-md px-3 py-2 text-[11px] leading-relaxed bg-cyan-500/5 border border-cyan-500/10 text-cyan-600 dark:text-cyan-400 flex items-start gap-1.5">
                <span>📅</span>
                <span><strong>Titration:</strong> {currentPreset.timeline[selectedTier!]}</span>
              </div>
            )}
            {currentPreset?.warnings?.[selectedTier!] && (
              <div className="mt-1.5 rounded-md px-3 py-2 text-[11px] leading-relaxed bg-rose-500/5 border border-rose-500/10 text-rose-600 dark:text-rose-400">
                {currentPreset.warnings[selectedTier!]}
              </div>
            )}
          </div>
        )}

        {/* Weight note */}
        {currentPreset?.weightNote && (
          <div className="mb-2 rounded-md px-3 py-2 text-[11px] leading-relaxed bg-amber-500/5 border border-amber-500/10 text-amber-600 dark:text-amber-400 flex items-start gap-1.5">
            <span>⚖️</span>
            <span><strong>Weight & Dosing:</strong> {currentPreset.weightNote}</span>
          </div>
        )}

        <div className="h-px bg-neutral-200 dark:bg-neutral-700 my-4" />

        {/* Form fields */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div className="col-span-2">
            <label className="block text-[10px] font-semibold uppercase tracking-wider text-neutral-500 dark:text-neutral-400 mb-1">Compound Name</label>
            <input
              type="text"
              value={formName}
              onChange={e => setFormName(e.target.value)}
              placeholder="e.g. Semaglutide"
              className="w-full bg-neutral-50 dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-700 rounded-lg px-2.5 py-2 text-sm font-mono outline-none focus:border-blue-500 transition-colors"
            />
          </div>
          <div>
            <label className="block text-[10px] font-semibold uppercase tracking-wider text-neutral-500 dark:text-neutral-400 mb-1">Concentration</label>
            <input
              type="number"
              value={formConc}
              onChange={e => setFormConc(e.target.value ? parseFloat(e.target.value) : '')}
              placeholder="e.g. 5"
              step="any"
              className="w-full bg-neutral-50 dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-700 rounded-lg px-2.5 py-2 text-sm font-mono outline-none focus:border-blue-500 transition-colors"
            />
          </div>
          <div>
            <label className="block text-[10px] font-semibold uppercase tracking-wider text-neutral-500 dark:text-neutral-400 mb-1">Conc. Unit</label>
            <select
              value={formConcUnit}
              onChange={e => setFormConcUnit(e.target.value as typeof formConcUnit)}
              className="w-full bg-neutral-50 dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-700 rounded-lg px-2.5 py-2 text-sm font-mono outline-none focus:border-blue-500 transition-colors"
            >
              <option value="mg/mL">mg/mL</option>
              <option value="mcg/mL">mcg/mL</option>
              <option value="units/mL">units/mL</option>
            </select>
          </div>
          <div>
            <label className="block text-[10px] font-semibold uppercase tracking-wider text-neutral-500 dark:text-neutral-400 mb-1">Vial Volume (mL)</label>
            <input
              type="number"
              defaultValue={formVial}
              key={`vial-${formVial}`}
              onChange={e => { const v = parseFloat(e.target.value); if (!isNaN(v)) setFormVial(v); }}
              onBlur={e => { const v = parseFloat(e.target.value); if (isNaN(v) || v <= 0) { setFormVial(5); e.target.value = '5'; } }}
              step="any"
              className="w-full bg-neutral-50 dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-700 rounded-lg px-2.5 py-2 text-sm font-mono outline-none focus:border-blue-500 transition-colors"
            />
          </div>
          <div>
            <label className="block text-[10px] font-semibold uppercase tracking-wider text-neutral-500 dark:text-neutral-400 mb-1">Desired Dose</label>
            <input
              type="number"
              value={formDose}
              onChange={e => setFormDose(e.target.value ? parseFloat(e.target.value) : '')}
              placeholder="e.g. 250"
              step="any"
              className="w-full bg-neutral-50 dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-700 rounded-lg px-2.5 py-2 text-sm font-mono outline-none focus:border-blue-500 transition-colors"
            />
          </div>
          <div>
            <label className="block text-[10px] font-semibold uppercase tracking-wider text-neutral-500 dark:text-neutral-400 mb-1">Dose Unit</label>
            <select
              value={formDoseUnit}
              onChange={e => setFormDoseUnit(e.target.value as typeof formDoseUnit)}
              className="w-full bg-neutral-50 dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-700 rounded-lg px-2.5 py-2 text-sm font-mono outline-none focus:border-blue-500 transition-colors"
            >
              <option value="mcg">mcg</option>
              <option value="mg">mg</option>
              <option value="units">units</option>
            </select>
          </div>
          <div>
            <label className="block text-[10px] font-semibold uppercase tracking-wider text-neutral-500 dark:text-neutral-400 mb-1">
              Frequency
              {currentPreset && formFreq === currentPreset.recFreq && (
                <span className="ml-1 text-amber-500">★</span>
              )}
            </label>
            <select
              value={formFreq}
              onChange={e => setFormFreq(e.target.value as FrequencyKey)}
              className="w-full bg-neutral-50 dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-700 rounded-lg px-2.5 py-2 text-sm font-mono outline-none focus:border-blue-500 transition-colors"
            >
              {Object.entries(FREQ_LABELS).map(([k, v]) => (
                <option key={k} value={k}>
                  {v}{currentPreset?.recFreq === k ? ' ★' : ''}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Live preview */}
        {livePreview && (
          <div className="mt-3">
            <label className="block text-[10px] font-semibold uppercase tracking-wider text-neutral-500 dark:text-neutral-400 mb-1.5">Dose Preview</label>
            <div className="grid grid-cols-4 gap-px bg-blue-500/5 border border-blue-500/10 rounded-lg overflow-hidden">
              <PreviewCell value={`${fmtMg(formDoseUnit === 'mg' ? (formDose as number) : (formDose as number) / 1000)} mg`} label="Milligrams" color="text-blue-500" />
              <PreviewCell value={`${(formDoseUnit === 'mcg' ? (formDose as number) : (formDose as number) * 1000).toFixed(0)} mcg`} label="Micrograms" color="text-violet-500" />
              <PreviewCell value={`${livePreview.vol.toFixed(livePreview.vol < 0.01 ? 4 : 3)} mL`} label="Volume" color="text-emerald-500" />
              <PreviewCell value={`${livePreview.units.toFixed(1)} u`} label="Insulin units" color="text-amber-500" />
            </div>
            <div className="grid grid-cols-3 gap-px bg-emerald-500/5 border border-emerald-500/10 rounded-lg overflow-hidden mt-1.5">
              <PreviewCell value={`${livePreview.totalMg >= 1 ? livePreview.totalMg.toFixed(1) : livePreview.totalMg.toFixed(3)} mg`} label="Vial Total" color="text-emerald-500" />
              <PreviewCell value={livePreview.dpv.toFixed(1)} label="Doses/Vial" color="text-cyan-500" />
              <PreviewCell value={livePreview.wpv.toFixed(1)} label="Wk/Vial" color="text-violet-500" />
            </div>
          </div>
        )}

        {/* Action buttons */}
        <div className="mt-4 flex gap-2">
          <button
            onClick={addCompound}
            disabled={!formName || !formConc || !formDose}
            className="inline-flex items-center gap-1.5 text-xs font-semibold px-4 py-2 rounded-lg bg-blue-500 text-white hover:bg-blue-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            <Plus className="w-3.5 h-3.5" />
            Add to Stack
          </button>
          <button
            onClick={clearForm}
            className="inline-flex items-center gap-1.5 text-xs font-semibold px-4 py-2 rounded-lg border border-neutral-200 dark:border-neutral-700 text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-700/30 transition-colors"
          >
            Clear
          </button>
        </div>
      </div>

      {/* Active Stack */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-bold flex items-center gap-2">
            <ClipboardList className="w-4 h-4" />
            Active Stack
          </h2>
          <span className="inline-flex items-center text-[10px] font-semibold px-2 py-0.5 rounded bg-blue-500/10 text-blue-500">
            {compounds.length} compound{compounds.length !== 1 ? 's' : ''}
          </span>
        </div>

        {compounds.length === 0 ? (
          <div className="bg-white dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 rounded-xl shadow-sm flex flex-col items-center justify-center py-12 px-5 text-center">
            <PlusCircle className="w-10 h-10 text-neutral-300 dark:text-neutral-600 mb-2.5" strokeWidth={1.2} />
            <p className="text-sm text-neutral-400">No compounds added yet</p>
            <p className="text-[11px] text-neutral-300 dark:text-neutral-600">Select a preset → pick a tier → add to stack</p>
          </div>
        ) : (
          compounds.map((c, i) => (
            <CompoundCard
              key={c.id}
              compound={c}
              index={i}
              isExpanded={expandedCards.has(c.id)}
              onToggle={() => toggleCard(c.id)}
              onRemove={() => removeCompound(c.id)}
              onUpdate={(field, value) => updateCompoundField(c.id, field, value)}
            />
          ))
        )}
      </div>

      {/* Summary */}
      {compounds.length > 0 && (
        <div className="bg-white dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 rounded-xl shadow-sm p-5">
          <h2 className="text-sm font-bold flex items-center gap-2 mb-4">
            <BarChart3 className="w-4 h-4" />
            Stack Summary
          </h2>
          <div className="grid grid-cols-3 gap-2.5 mb-4">
            <div className="text-center py-4 bg-neutral-50 dark:bg-neutral-900/50 rounded-lg border border-neutral-200 dark:border-neutral-700">
              <div className="font-mono text-lg font-semibold text-blue-500">{compounds.length}</div>
              <div className="text-[9px] text-neutral-400 uppercase tracking-wider mt-0.5">Compounds</div>
            </div>
            <div className="text-center py-4 bg-neutral-50 dark:bg-neutral-900/50 rounded-lg border border-neutral-200 dark:border-neutral-700">
              <div className="font-mono text-lg font-semibold text-amber-500">{summaryData.totalDailyInj.toFixed(1)}</div>
              <div className="text-[9px] text-neutral-400 uppercase tracking-wider mt-0.5">Avg Inj/Day</div>
            </div>
            <div className="text-center py-4 bg-neutral-50 dark:bg-neutral-900/50 rounded-lg border border-neutral-200 dark:border-neutral-700">
              <div className="font-mono text-[15px] font-semibold text-emerald-500">
                {summaryData.totalWkMg >= 1 ? `${summaryData.totalWkMg.toFixed(2)} mg` : `${(summaryData.totalWkMg * 1000).toFixed(0)} mcg`}
              </div>
              <div className="text-[9px] text-neutral-400 uppercase tracking-wider mt-0.5">Weekly Mass</div>
            </div>
          </div>
          <div className="flex flex-col gap-1.5">
            {compounds.map((c, i) => {
              const color = COMPOUND_COLORS[i % COMPOUND_COLORS.length];
              const r = calcCompound(c);
              const wd = r.wkMg >= 1 ? `${r.wkMg.toFixed(2)} mg` : `${(r.wkMg * 1000).toFixed(0)} mcg`;
              return (
                <div key={c.id} className="flex justify-between items-center px-3 py-2 bg-neutral-50 dark:bg-neutral-900/50 rounded-md">
                  <span className="text-[13px] font-semibold" style={{ color }}>{c.name}</span>
                  <span className="font-mono text-[11px] text-neutral-400">
                    {wd}/wk · {r.units.toFixed(1)}u × {r.pw}/wk
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Order Preview — formulary SKUs + cost for a chosen duration */}
      <OrderPreview stack={compounds} />

      {/* Stack Assessment */}
      <StackAssessment patient={metrics} stack={compounds} />

      {/* Disclaimer */}
      <div className="mt-6 px-4 py-3 rounded-xl bg-rose-500/5 border border-rose-500/10 text-[11px] text-rose-500 dark:text-rose-400 leading-relaxed opacity-80">
        <strong>Disclaimer:</strong> For research and compounding reference only. Dosing tiers sourced from published clinical trials, FDA prescribing information, and practitioner protocols. All calculations should be independently verified. Therapy should be supervised by a licensed healthcare professional.
      </div>
    </div>
  );
}

function PreviewCell({ value, label, color }: { value: string; label: string; color: string }) {
  return (
    <div className="py-2 px-1.5 text-center">
      <div className={`font-mono text-sm font-semibold ${color}`}>{value}</div>
      <div className="text-[9px] text-neutral-400 mt-0.5">{label}</div>
    </div>
  );
}
